"use client";
import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { HiMenuAlt4, HiX } from "react-icons/hi";
import { HiChevronDown } from "react-icons/hi";
import Image from "next/image";
import { useRouter, usePathname } from "@/navigation";
import { useLocale, useTranslations } from "next-intl";
import ThemeToggle from "@/components/ui/theme-toggle";
import navLinks from "@/lib/navLinks";
import Button from "@/components/ui/button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastY, setLastY] = useState(0);
  const [logoVisible, setLogoVisible] = useState(false);
  const pathname = usePathname();
  const t = useTranslations("common");
  const isHome = pathname === "/";
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (y) => {
    setHidden(y > lastY && y > 80);
    setLastY(y);
    if (isHome) setLogoVisible(y > 80);
  });

  useEffect(() => {
    if (!isHome) setLogoVisible(true);
  }, [isHome]);

  const navGroups = [
    { label: t("nav.services"), links: navLinks.services },
    { label: t("nav.industries"), links: navLinks.industries },
    { label: t("nav.about"), links: navLinks.about },
  ];

  return (
    <>
      <motion.header
        animate={{ y: hidden ? "-100%" : "0%" }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-md border-b border-white/[0.06]" />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-full">
          {/* Logo */}
          <motion.div
            animate={{ opacity: logoVisible ? 1 : 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link href="/" className="flex items-center gap-2">
              <Image
                src="/assets/logo/asynq_logo_transparent.png"
                alt="Asynq"
                width={90}
                height={28}
                className="h-7 w-auto invert dark:invert-0"
                priority
                fetchPriority="high"
              />
            </Link>
          </motion.div>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            <Link href="/ai-voice-agent">
              <span className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors rounded-lg hover:bg-violet-500/10">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                {t("nav.links.aiVoiceAgent")}
              </span>
            </Link>
            {navGroups.map((group) => (
              <DropdownGroup key={group.label} label={group.label} links={group.links} />
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden xl:flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link href="/contact">
              <Button variant="primary" size="sm">
                {t("cta.bookCall")}
              </Button>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <div className="flex xl:hidden items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-zinc-300"
              aria-label="Open menu"
            >
              <HiMenuAlt4 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-80 bg-zinc-950 border-l border-white/10 flex flex-col p-6"
            >
              <div className="flex items-center justify-between mb-8">
                <Image
                  src="/assets/logo/asynq_logo_transparent.png"
                  alt="Asynq"
                  width={80}
                  height={24}
                  className="h-6 w-auto"
                />
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-zinc-300"
                >
                  <HiX className="w-5 h-5" />
                </button>
              </div>

              <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
                {navGroups.map((group, i) => (
                  <motion.div
                    key={group.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider px-2 pt-4 pb-2">
                      {group.label}
                    </p>
                    {group.links.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setOpen(false)}
                        className="block px-2 py-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      >
                        {t(link.labelKey)}
                      </Link>
                    ))}
                  </motion.div>
                ))}
                <div className="pt-4 pb-1">
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider px-2 pb-2">Featured</p>
                  <Link
                    href="/ai-voice-agent"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 rounded-lg transition-colors"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                    {t("nav.links.aiVoiceAgent")}
                  </Link>
                </div>
                <div className="mt-auto pt-6 flex flex-col gap-3">
                  <LanguageSwitcher onNavigate={() => setOpen(false)} />
                  <Link href="/contact" onClick={() => setOpen(false)}>
                    <Button variant="primary" className="w-full justify-center">
                      {t("cta.bookCall")}
                    </Button>
                  </Link>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function LanguageSwitcher({ onNavigate } = {}) {
  const router = useRouter();
  const pathname = usePathname(); // returns path without locale prefix
  const currentLocale = useLocale();

  function switchLocale(locale) {
    router.replace(pathname, { locale });
    if (onNavigate) onNavigate();
  }

  return (
    <div className="flex items-center gap-0.5 text-xs font-medium">
      <button
        onClick={() => switchLocale("en")}
        className={`px-2 py-1 rounded transition-colors ${
          currentLocale === "en" ? "text-violet-400" : "text-zinc-500 hover:text-zinc-300"
        }`}
      >
        EN
      </button>
      <span className="text-zinc-700">|</span>
      <button
        onClick={() => switchLocale("de")}
        className={`px-2 py-1 rounded transition-colors ${
          currentLocale === "de" ? "text-violet-400" : "text-zinc-500 hover:text-zinc-300"
        }`}
      >
        DE
      </button>
    </div>
  );
}

function DropdownGroup({ label, links }) {
  const [open, setOpen] = useState(false);
  const t = useTranslations("common");

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button className="flex items-center gap-1 px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors rounded-lg hover:bg-black/5 dark:hover:bg-white/5">
        {label}
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <HiChevronDown className="w-3.5 h-3.5" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <div className="absolute top-full left-0 w-52 pt-2">
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="w-full rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-violet-500/10 p-2"
          >
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500/60" />
                {t(link.labelKey)}
              </Link>
            ))}
          </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
